import type {
  GroupPromoMetadataOutput,
  WonkaPromosOutputsGroupPromoOutput,
} from '@sunrise-ui/api/events/api';
import { QueryKeys } from '../constants/QueryKeys';
import { promosApi, useAuthMutation, useAuthQuery } from '../utils/api';
import {
  assertParameterIsNotEmptyString,
  assertParameterIsNotUndefinedOrNull,
} from '../utils/error';

const {
  promosKey,
  groupPromosKey,
  bySlugKey,
  byCommunityKey,
  myGroupPromosKey,
  bulkKey,
} = QueryKeys.promos;

export const useGetGroupPromoBySlug = (slug?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(slug, 'slug');
      const response =
        await promosApi.getGroupPromoBySlugPromosGroupPromosSlugGet({
          slug: slug!,
        });
      return response.data;
    },
    queryKey: [promosKey, groupPromosKey, bySlugKey, slug],
    enabled: Boolean(slug),
  });
};

export const useGetGroupPromoByEvent = ({
  eventId,
  communityId,
}: {
  eventId?: string;
  communityId?: string;
}) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await promosApi.getPromosByEventPromosEventsEventIdCommunitiesCommunityIdGet(
          {
            eventId,
            communityId,
          },
        );
      return response.data;
    },
    queryKey: [promosKey, groupPromosKey, bySlugKey, eventId],
    enabled: Boolean(eventId),
  });
};

export const useGetMyGroupPromos = (communityId?: string) => {
  return useAuthQuery<WonkaPromosOutputsGroupPromoOutput[]>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await promosApi.listMyGroupPromosPromosCommunitiesCommunityIdMyGroupPromosGet(
          {
            communityId: communityId!,
          },
        );
      return response.data;
    },
    queryKey: [
      promosKey,
      groupPromosKey,
      myGroupPromosKey,
      byCommunityKey,
      communityId,
    ],
    enabled: Boolean(communityId),
  });
};

export const useGetBulkGroupPromosByCommunities = (communityIds?: string[]) => {
  return useAuthQuery<GroupPromoMetadataOutput[]>({
    queryFn: async () => {
      if (!communityIds?.length) {
        return [];
      }
      const response =
        await promosApi.bulkListGroupPromosByCommunitiesPromosGroupPromosBulkPost(
          {
            bulkListGroupPromosInput: { community_ids: communityIds },
          },
        );
      return response.data;
    },
    queryKey: [
      promosKey,
      groupPromosKey,
      bulkKey,
      communityIds?.slice().sort(),
    ],
    enabled: Boolean(communityIds?.length),
  });
};

export type ValidateDiscountCodeArgs = {
  discountCode: string;
  ticketTypeIds: string[];
};

export const useValidateDiscountCode = () => {
  return useAuthMutation({
    async mutationFn({
      discountCode,
      ticketTypeIds,
    }: ValidateDiscountCodeArgs) {
      assertParameterIsNotEmptyString(discountCode, 'discountCode');

      const response =
        await promosApi.validateDiscountCodeForMultipleTicketTypesPromosValidateDiscountCodePost(
          {
            discountCode,
            validateMultiDiscountCodeInput: {
              ticket_type_ids: ticketTypeIds,
            },
          },
        );

      return response.data;
    },
  });
};
